import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Calendar as CalendarIcon, 
  List, 
  Loader2,
  Plus,
  Archive,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import confetti from "canvas-confetti";

import { SEO } from "@/components/seo/SEO";
import { ActionsHeader } from "@/components/actions/ActionsHeader";
import { ActionsTimeline } from "@/components/actions/ActionsTimeline";
import { ActionsCalendar } from "@/components/actions/ActionsCalendar";
import { ActionItem } from "@/components/actions/ActionCard";
import { CreateManualActionDialog } from "@/components/actions/CreateManualActionDialog";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";
import { useActionsData } from "@/hooks/useActionsData";
import type { Tables } from "@/integrations/supabase/types";

type Document = Tables<"documents">;

type FilterPeriod = 'all' | 'expired' | 'week' | 'month' | 'later';
type ViewMode = 'timeline' | 'calendar';

export default function Actions() {
  const navigate = useNavigate();
  const { openDocument } = useDocumentPreview();
  
  const { items, stats, loading, refetch } = useActionsData();
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showLater, setShowLater] = useState(false);

  // Separate "Plus tard" items (> 30 days) for collapsible section
  const { mainItems, laterItems } = useMemo(() => {
    const main: ActionItem[] = [];
    const later: ActionItem[] = [];
    
    items.forEach(item => {
      const days = item.daysUntilExpiry ?? 999;
      if (days > 30) {
        later.push(item);
      } else {
        main.push(item);
      }
    });
    
    return { mainItems: main, laterItems: later };
  }, [items]);

  // Filter items based on selected period
  const filteredItems = useMemo(() => {
    const baseItems = filterPeriod === 'later' ? laterItems : mainItems;
    
    if (filterPeriod === 'all' || filterPeriod === 'later') return baseItems;
    
    return baseItems.filter(item => {
      const days = item.daysUntilExpiry ?? 999;
      
      switch (filterPeriod) {
        case 'expired':
          return days < 0;
        case 'week':
          return days >= 0 && days <= 7;
        case 'month':
          return days > 7 && days <= 30;
        default:
          return true;
      }
    });
  }, [mainItems, laterItems, filterPeriod]);

  // Filter counts for badges
  const filterCounts = useMemo(() => ({
    all: mainItems.length,
    expired: mainItems.filter(i => (i.daysUntilExpiry ?? 999) < 0).length,
    week: mainItems.filter(i => {
      const d = i.daysUntilExpiry ?? 999;
      return d >= 0 && d <= 7;
    }).length,
    month: mainItems.filter(i => {
      const d = i.daysUntilExpiry ?? 999;
      return d > 7 && d <= 30;
    }).length,
    later: laterItems.length,
  }), [mainItems, laterItems]);

  const handleComplete = async (id: string) => {
    setUpdatingId(id);
    const item = items.find(i => i.id === id);
    
    // Handle fines separately
    if (id.startsWith('fine-')) {
      const fineId = id.replace('fine-', '');
      const { error } = await supabase
        .from("traffic_fines")
        .update({ status: "paid", payment_date: new Date().toISOString() })
        .eq("id", fineId);

      if (error) {
        toast.error("Erreur lors de la mise à jour");
      } else {
        confetti({ particleCount: 80, spread: 60, origin: { y: 0.7 } });
        toast.success("Amende marquée comme payée ! 🎉");
        refetch();
      }
      setUpdatingId(null);
      return;
    }
    
    if (item?.type === 'action') {
      const { error } = await supabase
        .from("document_actions")
        .update({ status: "done" })
        .eq("id", id);

      if (error) {
        toast.error("Erreur lors de la mise à jour");
      } else {
        confetti({ particleCount: 80, spread: 60, origin: { y: 0.7 } });
        toast.success("Action terminée ! 🎉");
        refetch();
      }
    } else {
      const { error } = await supabase
        .from("documents")
        .update({ expiry_dismissed_at: new Date().toISOString() })
        .eq("id", id);

      if (error) {
        toast.error("Erreur lors de la mise à jour");
      } else {
        confetti({ particleCount: 80, spread: 60, origin: { y: 0.7 } });
        toast.success("Document marqué comme traité ! 🎉");
        refetch();
      }
    }
    
    setUpdatingId(null);
  };

  const handleDismiss = async (id: string) => {
    setUpdatingId(id);
    const item = items.find(i => i.id === id);
    
    // Handle fines - mark as dismissed but keep in system
    if (id.startsWith('fine-')) {
      toast.info("Les amendes ne peuvent pas être ignorées - payez ou contestez");
      setUpdatingId(null);
      return;
    }
    
    if (item?.type === 'action') {
      const { error } = await supabase
        .from("document_actions")
        .update({ status: "dismissed" })
        .eq("id", id);

      if (error) {
        toast.error("Erreur lors de la mise à jour");
      } else {
        toast.success("Action ignorée");
        refetch();
      }
    } else {
      const { error } = await supabase
        .from("documents")
        .update({ expiry_dismissed_at: new Date().toISOString() })
        .eq("id", id);

      if (error) {
        toast.error("Erreur lors de la mise à jour");
      } else {
        toast.success("Alerte ignorée");
        refetch();
      }
    }
    
    setUpdatingId(null);
  };

  const handleAlreadyPaid = async (id: string) => {
    setUpdatingId(id);
    
    // Mark invoice as already paid (dismiss the alert)
    const { error } = await supabase
      .from("documents")
      .update({ expiry_dismissed_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      toast.error("Erreur lors de la mise à jour");
    } else {
      toast.success("Facture marquée comme déjà payée");
      refetch();
    }
    
    setUpdatingId(null);
  };

  const handleView = async (item: ActionItem) => {
    // For fines, navigate to the fleet fines page
    if (item.id.startsWith('fine-')) {
      navigate('/fleet/fines');
      return;
    }
    
    // Fetch complete document from database
    const { data: doc } = await supabase
      .from("documents")
      .select("*")
      .eq("id", item.documentId)
      .single();
    
    if (doc) {
      openDocument(doc);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-6 md:py-8">
      <SEO 
        title="Actions à faire - EasyFinder"
        description="Gérez vos échéances critiques et actions en attente"
        noIndex={true}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="text-muted-foreground"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-foreground">Échéances & Actions</h1>
            <p className="text-xs text-muted-foreground">
              {mainItems.length} alerte{mainItems.length !== 1 ? 's' : ''} active{mainItems.length !== 1 ? 's' : ''}
              {laterItems.length > 0 && ` · ${laterItems.length} plus tard`}
            </p>
          </div>
        </div>

        {/* View toggle + New action button */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/actions/archive')}
            className="gap-1.5 text-muted-foreground"
          >
            <Archive className="w-4 h-4" />
            <span className="hidden sm:inline">Archivées</span>
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowCreateDialog(true)}
            className="gap-1.5"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nouvelle action</span>
          </Button>
          <Button
            variant={viewMode === 'timeline' ? 'secondary' : 'ghost'}
            size="icon"
            onClick={() => setViewMode('timeline')}
          >
            <List className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'calendar' ? 'secondary' : 'ghost'}
            size="icon"
            onClick={() => setViewMode('calendar')}
          >
            <CalendarIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Create manual action dialog */}
      <CreateManualActionDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={refetch}
      />

      {/* Stats Header */}
      <ActionsHeader stats={stats} />

      {/* Filter Tabs */}
      <div className="mt-6 mb-4">
        <Tabs value={filterPeriod} onValueChange={(v) => setFilterPeriod(v as FilterPeriod)}>
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="all" className="relative">
              Actives
              {filterCounts.all > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-[10px]">
                  {filterCounts.all}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="expired" className="relative">
              En retard
              {filterCounts.expired > 0 && (
                <Badge variant="destructive" className="ml-1.5 h-5 px-1.5 text-[10px]">
                  {filterCounts.expired}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="week">
              Cette semaine
              {filterCounts.week > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-[10px] bg-warning/20 text-warning-foreground">
                  {filterCounts.week}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="month">
              Ce mois
              {filterCounts.month > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-[10px]">
                  {filterCounts.month}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="later">
              Plus tard
              {filterCounts.later > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-[10px]">
                  {filterCounts.later}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Main Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={viewMode + filterPeriod}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {viewMode === 'timeline' ? (
            <ActionsTimeline
              items={filteredItems}
              onComplete={handleComplete}
              onDismiss={handleDismiss}
              onView={handleView}
              updatingId={updatingId}
              onAlreadyPaid={handleAlreadyPaid}
            />
          ) : (
            <ActionsCalendar
              items={filteredItems}
              onItemClick={handleView}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Collapsible "Plus tard" section when on main view */}
      {filterPeriod === 'all' && laterItems.length > 0 && (
        <div className="mt-6">
          <Button
            variant="ghost"
            className="w-full justify-between text-muted-foreground hover:text-foreground"
            onClick={() => setShowLater(!showLater)}
          >
            <span className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4" />
              Plus tard ({laterItems.length})
            </span>
            {showLater ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
          
          <AnimatePresence>
            {showLater && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3"
              >
                <ActionsTimeline
                  items={laterItems}
                  onComplete={handleComplete}
                  onDismiss={handleDismiss}
                  onView={handleView}
                  updatingId={updatingId}
                  onAlreadyPaid={handleAlreadyPaid}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
